<template>
  <div
    class="prospect-row"
    :class="rowClass"
    role="button"
    tabindex="0"
    @click="$emit('open', family)"
    @keydown.enter.self="$emit('open', family)"
    @keydown.space.self.prevent="$emit('open', family)"
  >
    <div class="flex justify-space-between align-items-center gap-sm flex-wrap">
      <div class="flex-1" style="min-width: 0;">
        <div class="flex align-items-center gap-x-xs flex-wrap">
          <p class="prospect-row-headline">{{ family.contact_name }}</p>
          <span v-if="mode === 'queue' && isFreshInquiry" class="prospect-row-pill is-new">
            <i class="fa-solid fa-star"></i>
            NEW
          </span>
          <span v-if="mode === 'queue' && urgencyPillText" class="prospect-row-pill" :class="pillClass">
            <i v-if="urgencyIcon" :class="urgencyIcon"></i>
            {{ urgencyPillText }}
          </span>
          <span v-if="mode === 'all' && timeContextText" class="prospect-row-pill is-context">
            {{ timeContextText }}
          </span>
        </div>
        <p class="prospect-row-subline">
          <span v-if="family.assigned_coach">{{ family.assigned_coach.full_name }}</span>
          <span v-else class="text-gray-400">Unassigned</span>
        </p>
        <p
          class="prospect-row-last-contact"
          :class="{ 'is-empty': !family.last_contact }"
        >
          <i class="fa-solid fa-clock-rotate-left"></i>
          <template v-if="family.last_contact">
            Last: {{ activityTypeLabel(family.last_contact.activity_type) }}
            <span v-if="family.last_contact.author_name">by {{ family.last_contact.author_name }}</span>
            · {{ shortTimeAgo(family.last_contact.occurred_at) }}
          </template>
          <template v-else>Last: never contacted</template>
        </p>
        <ul class="prospect-wrestler-list">
          <li
            v-for="child in family.prospects"
            :key="child.id"
            class="prospect-wrestler-row"
          >
            <span class="prospect-wrestler-name">{{ child.child_name || 'Unnamed' }}</span>
            <span class="prospect-wrestler-stage" :class="stageBadgeClass(child.stage)">{{ childStageLabel(child) }}</span>

            <i
              v-if="mode === 'queue' && showAttentionDotFor(child)"
              class="fa-solid fa-circle prospect-wrestler-dot"
              :title="attentionReason(child) || 'Needs follow-up'"
            ></i>
          </li>
        </ul>
      </div>

      <div class="flex align-items-center gap-x-xs flex-shrink-0 flex-0-0-100-xs-only" style="position: relative;" @click.stop>
        <a v-if="telHref" :href="telHref" class="btn btn-default btn-sm" title="Call">
          <i class="fa-solid fa-phone"></i>
        </a>
        <a
          v-else-if="suggestedTelHref"
          :href="suggestedTelHref"
          class="btn btn-default btn-sm prospect-suggested-contact"
          :title="suggestedPhoneTitle('Call')"
        >
          <i class="fa-solid fa-phone"></i><i class="fa-solid fa-wand-magic-sparkles prospect-suggested-wand"></i>
        </a>
        <button v-else type="button" class="btn btn-default btn-sm" disabled title="No phone on file">
          <i class="fa-solid fa-phone"></i>
        </button>

        <a v-if="smsHref" :href="smsHref" class="btn btn-default btn-sm" title="Text">
          <i class="fa-regular fa-comment"></i>
        </a>
        <a
          v-else-if="suggestedSmsHref"
          :href="suggestedSmsHref"
          class="btn btn-default btn-sm prospect-suggested-contact"
          :title="suggestedPhoneTitle('Text')"
        >
          <i class="fa-regular fa-comment"></i><i class="fa-solid fa-wand-magic-sparkles prospect-suggested-wand"></i>
        </a>
        <button v-else type="button" class="btn btn-default btn-sm" disabled title="No phone on file">
          <i class="fa-regular fa-comment"></i>
        </button>

        <a v-if="mailtoHref" :href="mailtoHref" class="btn btn-default btn-sm" title="Email">
          <i class="fa-solid fa-envelope"></i>
        </a>
        <button v-else type="button" class="btn btn-default btn-sm" disabled title="No email on file">
          <i class="fa-solid fa-envelope"></i>
        </button>

        <message-shortcut-button
          v-if="canDirectMessage"
          :profile-id="family.guardian.id"
          :profile-type="family.guardian.type"
          :profile-name="family.guardian.full_name"
          :current-profile-id="currentAuthorId"
          :current-profile-type="currentAuthorType"
          :show-label="false"
          button-class="btn btn-default btn-sm"
          button-title="Message in WIQ"
          icon-class="fa-solid fa-comments"
        />
        <button
          v-else
          type="button"
          class="btn btn-default btn-sm"
          disabled
          :title="dmDisabledTitle"
        >
          <i class="fa-solid fa-comments"></i>
        </button>

        <button
          v-if="showLogContact"
          type="button"
          class="btn btn-primary btn-sm m-l-xs"
          title="Log contact"
          @click.stop="togglePopover"
        >
          <i class="fa-regular fa-comment-dots"></i> Log contact
        </button>

        <log-contact-popover
          v-if="showLogPopover"
          :create-url="familyNotesUrl"
          :family="family"
          @cancel="showLogPopover = false"
          @logged="onLogged"
          @stage-change="$emit('stage-change', $event)"
        />
      </div>

      <i class="fa-solid fa-chevron-right prospect-row-chevron hidden-xs" aria-hidden="true"></i>
    </div>
  </div>
</template>

<script>
import LogContactPopover from './log_contact_popover.vue';
import MessageShortcutButton from './message_shortcut_button.vue';
import {
  stageLabel, childStageLabel, followUpReasonLabel, suggestionPromptLabel,
} from './prospectStageLabels';

// Normalize a parent-entered phone string to E.164-ish for tel:/sms: links.
// Strips formatting; assumes US +1 for bare 10-digit numbers.
function normalizePhoneDigits(raw) {
  if (!raw) return '';
  const cleaned = String(raw).replace(/[^\d+]/g, '');
  if (cleaned.startsWith('+')) return cleaned;
  if (cleaned.length === 10) return `+1${cleaned}`;
  if (cleaned.length === 11 && cleaned.startsWith('1')) return `+${cleaned}`;
  return cleaned;
}

export default {
  name: 'prospect-row',
  components: { LogContactPopover, MessageShortcutButton },
  props: {
    family: { type: Object, required: true },
    familiesApiUrl: { type: String, required: true },
    teamName: { type: String, default: '' },
    currentAuthorId: { type: Number, default: null },
    currentAuthorType: { type: String, default: '' },
    urgency: {
      type: String,
      default: null,
      validator: (v) => ['overdue', 'today', 'upcoming-trial', null].includes(v),
    },
    mode: {
      type: String,
      default: 'queue',
      validator: (v) => ['queue', 'all'].includes(v),
    },
  },
  data() {
    return {
      showLogPopover: false,
    };
  },
  computed: {
    familyNotesUrl() {
      return `${this.familiesApiUrl}/${this.family.id}/notes`;
    },
    phoneDigits() {
      return normalizePhoneDigits(this.family.contact_phone);
    },
    telHref() {
      return this.phoneDigits ? `tel:${this.phoneDigits}` : '';
    },
    smsHref() {
      return this.phoneDigits ? `sms:${this.phoneDigits}` : '';
    },
    // Best phone found on a linked profile's registration answers, served by
    // the API for blank-phone families. Lets a coach dial straight from the
    // row; saving it onto the lead still happens in the drawer.
    firstPhoneSuggestion() {
      if (this.phoneDigits) return null;
      const list = this.family.phone_suggestions || [];
      return list.length ? list[0] : null;
    },
    suggestedPhoneDigits() {
      return this.firstPhoneSuggestion ? normalizePhoneDigits(this.firstPhoneSuggestion.value) : '';
    },
    suggestedTelHref() {
      return this.suggestedPhoneDigits ? `tel:${this.suggestedPhoneDigits}` : '';
    },
    suggestedSmsHref() {
      return this.suggestedPhoneDigits ? `sms:${this.suggestedPhoneDigits}` : '';
    },
    mailtoHref() {
      if (!this.family.contact_email) return '';
      if (!this.teamName) return `mailto:${this.family.contact_email}`;
      const subject = encodeURIComponent(`Wrestling at ${this.teamName}`);
      return `mailto:${this.family.contact_email}?subject=${subject}`;
    },
    urgencyClass() {
      if (this.urgency === 'overdue') return 'is-overdue';
      if (this.urgency === 'today') return 'is-today';
      if (this.urgency === 'upcoming-trial') return 'is-upcoming-trial';
      return '';
    },
    // Only the queue applies the colored left edge stripe; All Leads is urgency-free.
    rowClass() {
      return this.mode === 'queue' ? this.urgencyClass : '';
    },
    urgencyIcon() {
      if (this.urgency === 'overdue') return 'fa-solid fa-circle-exclamation';
      if (this.urgency === 'today') return 'fa-regular fa-bell';
      if (this.urgency === 'upcoming-trial') return 'fa-solid fa-calendar-days';
      return '';
    },
    pillClass() {
      if (this.urgency === 'overdue') return 'is-overdue';
      if (this.urgency === 'today') return 'is-today';
      if (this.urgency === 'upcoming-trial') return 'is-upcoming-trial';
      return '';
    },
    mostUrgentChild() {
      if (!this.family.prospects || this.family.prospects.length === 0) return null;
      const flagged = this.family.prospects.filter((c) => c.needs_follow_up);
      if (flagged.length > 0) {
        // Oldest follow-up flag wins.
        return flagged.reduce((oldest, c) => {
          const a = oldest.follow_up_set_at ? new Date(oldest.follow_up_set_at).getTime() : 0;
          const b = c.follow_up_set_at ? new Date(c.follow_up_set_at).getTime() : 0;
          return b && (!a || b < a) ? c : oldest;
        });
      }
      const trialChildren = this.family.prospects
        .filter((c) => c.trial_scheduled_at)
        .sort((a, b) => new Date(a.trial_scheduled_at) - new Date(b.trial_scheduled_at));
      if (trialChildren.length > 0) return trialChildren[0];
      return this.family.prospects[0];
    },
    isFreshInquiry() {
      const child = this.mostUrgentChild;
      if (!child || !child.needs_follow_up) return false;
      if (child.follow_up_reason !== 'inquiry') return false;
      return this.daysSinceFollowUp(child) === 0;
    },
    urgencyPillText() {
      if (this.urgency === 'upcoming-trial') return this.trialPillText();
      if (this.family.needs_attention && this.mostUrgentChild) {
        const reason = this.attentionReason(this.mostUrgentChild);
        if (!reason) return '';
        if (this.family.prospects.length > 1) {
          return `${this.mostUrgentChild.child_name}: ${reason}`;
        }
        return reason;
      }
      return '';
    },
    // For All Leads: pick the wrestler with the most recent stage change to
    // drive the time-context line (most recent activity wins).
    primaryChild() {
      if (!this.family.prospects || this.family.prospects.length === 0) return null;
      return [...this.family.prospects].sort((a, b) => {
        const aD = a.stage_changed_at ? new Date(a.stage_changed_at).getTime() : 0;
        const bD = b.stage_changed_at ? new Date(b.stage_changed_at).getTime() : 0;
        return bD - aD;
      })[0];
    },
    timeContextText() {
      const child = this.primaryChild;
      if (!child) return '';
      const text = this.timeContextFor(child);
      if (!text) return '';
      if (this.family.prospects.length > 1) return `${child.child_name}: ${text}`;
      return text;
    },
    // In All Leads, hide Log Contact for terminal stages — outreach is past tense.
    showLogContact() {
      if (this.mode !== 'all') return true;
      const stage = this.primaryChild ? this.primaryChild.stage : null;
      return !['converted', 'didnt_join', 'archived'].includes(stage);
    },
    // WIQ DM is only possible when the family has a linked profile we can
    // address (parent or coach). Otherwise we render a disabled affordance.
    canDirectMessage() {
      if (!this.family.guardian) return false;
      if (!this.currentAuthorId || !this.currentAuthorType) return false;
      return ['ParentProfile', 'CoachProfile'].includes(this.family.guardian.type);
    },
    dmDisabledTitle() {
      if (!this.family.guardian) return 'No linked parent or coach account';
      return 'Cannot send a WIQ message';
    },
  },
  methods: {
    togglePopover() {
      this.showLogPopover = !this.showLogPopover;
    },
    // Tooltip spelling out where the suggested number came from, so a coach
    // can judge it (e.g. a "Parent 2" answer) before dialing.
    suggestedPhoneTitle(verb) {
      const s = this.firstPhoneSuggestion;
      if (!s) return '';
      let role = 'Parent';
      if (s.relation === 'wrestler') {
        role = 'Wrestler';
      } else if (s.profile_type === 'CoachProfile') {
        role = 'Coach';
      }
      return `${verb} ${s.value} — ${suggestionPromptLabel(s)} (${role}: ${s.profile_name}). Open the lead to save it.`;
    },
    onLogged() {
      this.showLogPopover = false;
      this.$emit('note-created', this.family);
    },
    stageLabel(stage) {
      return stageLabel(stage);
    },
    // Derives "Needs Scheduling" for a purchased-but-undated trial.
    childStageLabel(child) {
      return childStageLabel(child);
    },
    activityTypeLabel(value) {
      const labels = {
        phone_call: 'Phone Call',
        sms: 'Text',
        email: 'Email',
        in_person: 'In Person',
        other: 'Contact',
      };
      return labels[value] || 'Contact';
    },
    // Tints the wrestler-row stage chip by stage. Same palette as the drawer's
    // stage badge, so the same color reads as the same stage everywhere a coach
    // sees stage state. Falls back to neutral gray for unknown stages.
    stageBadgeClass(stage) {
      const classes = {
        inquiry: 'bg-blue-100 text-blue-800',
        trial_scheduled: 'bg-purple-100 text-purple-800',
        trialing: 'bg-green-100 text-green-800',
        trial_complete: 'bg-yellow-100 text-yellow-800',
        converted: 'bg-green-500 color-white',
        didnt_join: 'bg-red-100 text-red-800',
        archived: 'bg-gray-100 text-gray-800',
      };
      return classes[stage] || 'bg-gray-100 text-gray-800';
    },
    showAttentionDotFor(child) {
      // Only differentiate WITHIN a multi-wrestler family — single-wrestler families
      // already have the family-level pill carrying the urgency.
      if (this.family.prospects.length <= 1) return false;
      if (!child.needs_follow_up) return false;
      return this.mostUrgentChild && child.id === this.mostUrgentChild.id;
    },
    attentionReason(child) {
      if (!child.needs_follow_up) return '';
      const reasonLabel = this.reasonLabel(child.follow_up_reason);
      const days = this.daysSinceFollowUp(child);
      if (!child.follow_up_set_at) return reasonLabel || 'Needs follow-up';
      if (days === 0) return reasonLabel ? `${reasonLabel} today` : 'Flagged today';
      const suffix = `${days} ${days === 1 ? 'day' : 'days'} ago`;
      return reasonLabel ? `${reasonLabel} · ${suffix}` : `Flagged ${suffix}`;
    },
    reasonLabel(reason) {
      return followUpReasonLabel(reason);
    },
    daysSinceFollowUp(child) {
      if (!child.follow_up_set_at) return 0;
      return Math.floor((new Date() - new Date(child.follow_up_set_at)) / 86400000);
    },
    shortTimeAgo(dateString) {
      if (!dateString) return '';
      const diffMs = new Date() - new Date(dateString);
      const mins = Math.floor(diffMs / 60000);
      const hours = Math.floor(diffMs / 3600000);
      const days = Math.floor(diffMs / 86400000);
      if (mins < 1) return 'just now';
      if (mins < 60) return `${mins}m ago`;
      if (hours < 24) return `${hours}h ago`;
      if (days === 1) return '1d ago';
      return `${days}d ago`;
    },
    // All-Leads time context — relative for active stages, absolute for terminal stages.
    timeContextFor(child) {
      if (!child) return '';
      const { stage } = child;
      if (stage === 'inquiry') {
        const d = this.daysSince(child.created_at);
        if (d <= 0) return 'Inquired today';
        if (d === 1) return 'Inquired 1d ago';
        return `Inquired ${d}d ago`;
      }
      if (stage === 'trial_scheduled') {
        if (child.trial_scheduled_at) return `Trial ${this.formatDateShort(child.trial_scheduled_at)}`;
        return 'Trial date not set';
      }
      if (stage === 'trialing') {
        const d = this.daysSince(child.stage_changed_at);
        if (d <= 0) return 'Started trialing today';
        if (d === 1) return 'Trialing 1 day';
        return `Trialing ${d} days`;
      }
      if (stage === 'trial_complete') {
        const date = child.trial_completed_at || child.stage_changed_at;
        const d = this.daysSince(date);
        if (d <= 0) return 'Trial completed today';
        if (d === 1) return 'Trial completed 1d ago';
        return `Trial completed ${d}d ago`;
      }
      if (stage === 'converted') {
        const date = child.converted_at || child.stage_changed_at;
        return date ? `Converted ${this.formatDate(date)}` : 'Converted';
      }
      if (stage === 'didnt_join') {
        const date = child.stage_changed_at;
        if (child.lost_reason) {
          return date
            ? `Lost: ${child.lost_reason} (${this.formatDate(date)})`
            : `Lost: ${child.lost_reason}`;
        }
        return date ? `Didn't join ${this.formatDate(date)}` : "Didn't join";
      }
      if (stage === 'archived') {
        const date = child.archived_at || child.stage_changed_at;
        return date ? `Archived ${this.formatDate(date)}` : 'Archived';
      }
      return '';
    },
    daysSince(dateString) {
      if (!dateString) return 0;
      return Math.floor((new Date() - new Date(dateString)) / 86400000);
    },
    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString(undefined, {
        month: 'short', day: 'numeric', year: 'numeric',
      });
    },
    formatDateShort(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
      });
    },
    trialPillText() {
      const child = this.mostUrgentChild;
      if (!child || !child.trial_scheduled_at) return '';
      const diffMs = new Date(child.trial_scheduled_at) - new Date();
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      const prefix = this.family.prospects.length > 1 ? `${child.child_name}: ` : '';
      if (diffHours < 0) return `${prefix}trial in progress`;
      if (diffHours < 24) {
        const timeStr = new Date(child.trial_scheduled_at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
        return `${prefix}trial today ${timeStr}`;
      }
      if (diffDays === 1) return `${prefix}trial tomorrow`;
      return `${prefix}trial in ${diffDays} days`;
    },
  },
};
</script>
